import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  CalendarIcon,
  VideoCameraIcon,
  MapPinIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { format } from 'date-fns';
import { fr, es, enUS } from 'date-fns/locale';
import { appointmentsAPI } from '../../utils/api';

const RecentInvitations = ({ showToast }) => {
  const { t } = useLanguage();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRecentInvitations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await appointmentsAPI.getRecentInvitations({ limit: 20 });
      setInvitations(response.data.appointments || []);
    } catch (error) {
      console.error('Error fetching recent invitations:', error);
      showToast(t('appointments.recentInvitations.error.fetch', 'Erreur lors du chargement'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    fetchRecentInvitations();
  }, [fetchRecentInvitations]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRecentInvitations();
    setRefreshing(false);
    showToast(t('appointments.recentInvitations.refreshed', 'Liste actualisée'), 'success');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return t('appointments.status.confirmed', 'Confirmé');
      case 'pending':
        return t('appointments.status.pending', 'En attente');
      case 'cancelled':
        return t('appointments.status.cancelled', 'Annulé');
      default:
        return status;
    }
  };

  const getMeetingTypeIcon = (type) => {
    return type === 'online' ? 
      <VideoCameraIcon className="h-4 w-4 text-blue-600" /> : 
      <MapPinIcon className="h-4 w-4 text-gray-600" />;
  };

  const formatDate = (date) => {
    const locale = localStorage.getItem('language') === 'es' ? es : 
                  localStorage.getItem('language') === 'en' ? enUS : fr;
    return format(new Date(date), 'dd MMM yyyy', { locale });
  };

  const formatTime = (time) => {
    return time ? time.substring(0, 5) : '';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-full flex flex-col">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-primary-500" />
            <h2 className="text-xl font-bold text-gray-900">
              {t('appointments.recentInvitations.title', 'Derniers rendez-vous envoyés')}
            </h2>
            {invitations.length > 0 && (
              <span className="ml-2 text-sm text-gray-500">({invitations.length})</span>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50 transition-colors"
            aria-label={t('common.refresh')}
          >
            {refreshing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            ) : (
              t('common.refresh', 'Actualiser')
            )}
          </button>
        </div>
      </div>

      <div className="card-content flex-1 flex flex-col min-h-0">
        {invitations.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <ExclamationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">
                {t('appointments.recentInvitations.empty', 'Aucune invitation envoyée récemment')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto pr-2 custom-scrollbar" style={{ maxHeight: 'calc(100vh - 24rem)' }}>
            {invitations.map((invitation, index) => (
              <motion.div
                key={invitation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getStatusIcon(invitation.status)}
                      <span className="font-medium text-gray-900 text-sm">
                        {invitation.candidate_name}
                      </span>
                      {getMeetingTypeIcon(invitation.meeting_type)}
                    </div>
                    
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{invitation.candidate_email}</span>
                      </div>
                      
                      {invitation.offer_title && (
                        <div className="flex items-center">
                          <BriefcaseIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{invitation.offer_title}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center">
                        <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400 flex-shrink-0" />
                        <span>{formatDate(invitation.date)} {t('common.at', 'à')} {formatTime(invitation.start_time)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4 text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invitation.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      invitation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {getStatusLabel(invitation.status)}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('appointments.recentInvitations.sentOn', 'Envoyé le')} {formatDate(invitation.created_at)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentInvitations;
